import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Divider,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  IconButton,
  Alert,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import {
  getInputProps,
  getInputLabelProps,
} from "../../../../common/appStyles";
import PageTitleSubtitle from "../../../../components/PageTitleSubTitle";
import localize from "../../../../common/localize";
import ModalBackground from "../../../../components/common/ModalBackground";
import FieldEditor from "./FieldEditor";
import DynamicFormFieldImageThumbnail from "./DynamicFormFieldImageThumbnail";
import { getClearFieldValueModel } from "../../../../common/EntitiesManagement";
import ErrorMessage from "../../../../components/common/ErrorMessage";
import { createEntity, updateEntity } from "../../../../context/GlobalContext";

export default function DynamicFormPageReview({
  formPage,
  statusChangeId,
  eventId,
  dynamicFormModel,
  allFieldsDisabled,
}) {
  const {
    DYNAMIC_FORM_ENDPOINT,
    DYNAMIC_FORM_FIELD_VALUE_ENDPOINT,
    EVENTS_API,
    config,
  } = useGlobalContext();
  const [dynamicFormFields, setDynamicFormFields] = useState();
  const [pageValues, setPageValues] = useState();
  const [dynamicFormFieldValues, setDynamicFormFieldValues] = useState();
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [showFieldEditionModal, setShowFieldEditionModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showUploadingImage, setShowUploadingImage] = useState(false);
  const [showNoDataAlert, setShowNoDataAlert] = useState(false);

  const getDynamicFormPageFields = async () => {
    try {
      const data = await axios.get(
        `${DYNAMIC_FORM_ENDPOINT}/${dynamicFormModel.Id}/page/${formPage.Id}/field`,
        config
      );
      setDynamicFormFields(data?.data?.Data);
    } catch (error) {
      setErrorMessage(`${localize.error_unexpected}: ${error}`);
      setShowErrorModal(true);
      setDynamicFormFields([]);
    }
  };

  const getDynamicFormFieldValues = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${eventId}/page/${formPage.Id}/status-change/${statusChangeId}/dynamic-form-field-values`,
        config
      );
      const fieldsData = data?.data?.Data;

      setPageValues(fieldsData);
    } catch (error) {
      setErrorMessage(`${localize.error_unexpected}: ${error}`);
      setShowErrorModal(true);
      setPageValues([]);
    }
  };

  const getDynamicFormFieldValuesWithoutStatusChange = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${eventId}/page/${formPage.Id}/no-status-change/dynamic-form-field-values`,
        config
      );
      setPageValues(data?.data?.Data);
    } catch (error) {
      setErrorMessage(`${localize.error_unexpected}: ${error}`);
      setShowErrorModal(true);
      setPageValues([]);
    }
  };

  const generateDynamicFormFieldsOnPageValue = () => {
    const currentFields = dynamicFormFieldValues;

    setDynamicFormFieldValues(
      currentFields.map((x) => constructWithFieldValue(x))
    );
  };

  const constructWithFieldValue = (field) => {
    const fieldValue = pageValues?.find(
      (x) => x?.FormFieldOnPage?.FormField?.Id === field?.FormField?.Id
    );

    return {
      ...field,
      FieldValue: {
        Id: fieldValue?.Id,
        Value: fieldValue?.FieldValue,
        OriginalValue: fieldValue?.OriginalFieldValue,
        ModifiedBy: fieldValue?.ModifiedBy,
        ModificationDate: fieldValue?.ModificationDate,
        FromWeb: fieldValue?.FromWeb,
      },
    };
  };

  useEffect(() => {
    if (dynamicFormFields && dynamicFormFields.length > 0) {
      setDynamicFormFieldValues(dynamicFormFields);

      return;
    }

    setDynamicFormFieldValues([]);
  }, [dynamicFormFields]);

  useEffect(() => {
    setShowNoDataAlert(false);
    if (pageValues && pageValues.length > 0) {
      generateDynamicFormFieldsOnPageValue();
      return;
    }

    setShowNoDataAlert(true);
  }, [pageValues]);

  useEffect(() => {
    setPageValues([]);

    if (dynamicFormModel && formPage) reloadPage();
  }, [dynamicFormModel, formPage]);

  const reloadPage = async () => {
    if (dynamicFormModel && formPage) {
      await getDynamicFormPageFields();

      if (statusChangeId) {
        await getDynamicFormFieldValues();
      } else {
        await getDynamicFormFieldValuesWithoutStatusChange();
      }
    }
  };

  const mustShowEditButton = () => !allFieldsDisabled;

  const getBooleanFieldText = (options, checked) => {
    if (options) {
      const optionsTexts = JSON.parse(options);
      if (optionsTexts) {
        return checked ? optionsTexts.trueText : optionsTexts.falseText;
      }
    }

    return checked ? localize.yes : localize.no;
  };

  const handleFieldValueUpdateOrCreate = () => {
    const fieldValueRequest = {
      ...getClearFieldValueModel(
        parseInt(eventId),
        fieldToEdit?.FormField?.Id,
        formPage?.Id
      ),
      FieldValue: fieldToEdit?.FieldValue?.Value,
      FromWeb: true,
      StatusChangeUUID: `${statusChangeId ?? 0}`,
    };

    if (fieldToEdit?.FieldValue.Id > 0) {
      updateFieldValue(fieldValueRequest, fieldToEdit?.FieldValue.Id);
    } else {
      createFieldValue(fieldValueRequest);
    }

    setFieldToEdit(null);
  };

  const validateOperationResult = (result) => {
    setShowUploadingImage(false);
    if (!result.ok && !result.Ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    reloadPage();
  };

  const fileUpload = async (id, file, fieldValue) => {
    const url =
      id > 0
        ? `${DYNAMIC_FORM_FIELD_VALUE_ENDPOINT}/${id}/file`
        : `${DYNAMIC_FORM_FIELD_VALUE_ENDPOINT}/file`;
    const formData = new FormData();
    formData.append(
      "requestData",
      new Blob([JSON.stringify(fieldValue)], {
        type: "application/json",
      })
    );
    formData.append("document", file, file.name);

    const requestConfig = {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    };

    const requestResult =
      id > 0
        ? await axios.put(url, formData, requestConfig)
        : await axios.post(url, formData, requestConfig);

    return requestResult?.data;
  };

  const createFieldValue = async (value) => {
    if (
      fieldToEdit?.FormField?.ValueType === "PHOTO" ||
      fieldToEdit?.FormField?.ValueType === "SIGNATURE"
    ) {
      setShowUploadingImage(true);
      const fileUploadResult = await fileUpload(0, value.FieldValue, {
        ...value,
        FieldValue: "",
      });
      validateOperationResult(fileUploadResult);
      return;
    }

    const result = await createEntity(
      DYNAMIC_FORM_FIELD_VALUE_ENDPOINT,
      value,
      config
    );
    validateOperationResult(result);
  };

  const updateFieldValue = async (value, fieldId) => {
    const valueToUpdate = {
      ...value,
      // FromWeb: false,
    };

    if (
      fieldToEdit?.FormField?.ValueType === "PHOTO" ||
      fieldToEdit?.FormField?.ValueType === "SIGNATURE"
    ) {
      setShowUploadingImage(true);
      const fileUploadResult = await fileUpload(
        fieldId,
        valueToUpdate.FieldValue,
        {
          ...valueToUpdate,
          FieldValue: "",
        }
      );
      validateOperationResult(fileUploadResult);

      return;
    }

    const result = await updateEntity(
      `${DYNAMIC_FORM_FIELD_VALUE_ENDPOINT}/${fieldId}`,
      valueToUpdate,
      config
    );
    validateOperationResult(result);
  };

  const mustShowModificationData = (fieldItem) =>
    fieldItem?.FieldValue?.FromWeb;

  return (
    <div>
      {dynamicFormFieldValues?.length > 0 ? (
        <div
          style={{ width: "100%", maxWidth: statusChangeId ? "800px" : "100%" }}
          className="flex-col w-full"
        >
          {showUploadingImage && (
            <ModalBackground
              showModal={showUploadingImage}
              setShowModal={setShowUploadingImage}
              title={localize.app_name}
              hideCancelButton
              hideSubmitButton
              Component={
                <div style={{ width: "300px" }} className="felx-col">
                  <Typography>{`${localize.upload_image}. ${localize.please_wait}...`}</Typography>
                </div>
              }
            />
          )}

          {showErrorModal && (
            <ErrorMessage
              message={errorMessage}
              title={localize.error_unexpected}
              showModal={showErrorModal}
              setShowModal={setShowErrorModal}
            />
          )}

          {showFieldEditionModal && (
            <ModalBackground
              isDrawerMode
              title={localize.edit_value}
              showModal={showFieldEditionModal}
              setShowModal={setShowFieldEditionModal}
              onClickToCancel={() => {
                setShowFieldEditionModal(false);
                setFieldToEdit(null);
              }}
              onClickToSubmit={() => {
                setShowFieldEditionModal(false);
                handleFieldValueUpdateOrCreate();
              }}
              Component={
                <FieldEditor field={fieldToEdit} setField={setFieldToEdit} />
              }
              customHeight={"95%"}
            />
          )}

          {showNoDataAlert && (
            <Alert severity="warning" className="w-full my-2">
              <label className="text-xs">{localize.no_data_on_form_page}</label>
            </Alert>
          )}

          {dynamicFormFieldValues?.length > 0 &&
            dynamicFormFieldValues.map((item) => (
              <div key={item.Id}>
                <Divider />
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", my: 1, width: "100%" }}>
                  <Box sx={{ width: { xs: "100%", sm: "50%" }, mr: { xs: 0, sm: "15px" }, mb: { xs: 1, sm: 0 } }}>
                    <PageTitleSubtitle
                      useEventTitleStyle
                      title={item?.FormField?.Title}
                      subtitle={item?.FormField?.Description}
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", width: { xs: "100%", sm: "50%" } }}>
                    <div className="flex-col w-full">
                      <div className="flex w-full justify-between">
                        <div className="flex w-full">
                          {item?.Mandatory && (
                            <div className="mr-2">
                              <Typography style={{ color: "red" }}>
                                <b>*</b>
                              </Typography>
                            </div>
                          )}
                          {item?.FormField?.ValueType === "BOOLEAN" ? (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={item?.FieldValue?.Value === "1"}
                                  style={{
                                    marginLeft: item?.Mandatory
                                      ? "0px"
                                      : "15px",
                                  }}
                                />
                              }
                              label={getBooleanFieldText(
                                item?.FormField?.Options,
                                item?.FieldValue?.Value === "1"
                              )}
                            />
                          ) : item?.FormField?.ValueType === "SIGNATURE" ||
                            item?.FormField?.ValueType === "PHOTO" ? (
                            <DynamicFormFieldImageThumbnail
                              fieldValueId={item?.FieldValue?.Id}
                              disabled={!item?.FieldValue?.Id}
                              buttonStyle={{
                                width: "250px",
                                marginRight: "5px",
                                marginLeft: item?.Mandatory ? "0px" : "15px",
                              }}
                              onOpen={() => {
                                window.open(
                                  `${
                                    window.location.href
                                  }/dynamic-form-field-value/${
                                    item?.FieldValue?.Id
                                  }/false/${
                                    item?.FormField?.ValueType === "SIGNATURE"
                                      ? false
                                      : item?.Multiline === true
                                  }`,
                                  "_blank"
                                );
                              }}
                            />
                          ) : (
                            <TextField
                              name={`${item?.FieldValue?.Id}`}
                              value={item?.FieldValue?.Value ?? ""}
                              multiline={item?.Multiline}
                              rows={item?.Multiline ? 3 : 1}
                              onChange={() => {}}
                              style={{
                                width: "100%",
                                marginRight: "5px",
                                marginLeft: item?.Mandatory ? "0px" : "15px",
                              }}
                              size="small"
                              InputLabelProps={getInputLabelProps()}
                              inputProps={{
                                ...getInputProps(),
                                readOnly: true,
                              }}
                            />
                          )}
                        </div>

                        {mustShowEditButton() && (
                          <div
                            style={{ alignSelf: "center", justifySelf: "end" }}
                          >
                            <IconButton
                              onClick={() => {
                                setFieldToEdit(item);
                                setShowFieldEditionModal(true);
                              }}
                            >
                              <Edit
                                style={{
                                  color: "#10366E",
                                  width: "18px",
                                  height: "18px",
                                }}
                              />
                            </IconButton>
                          </div>
                        )}
                      </div>

                      {mustShowModificationData(item) && (
                        <div style={{ marginLeft: "5px" }}>
                          <label className="page_description">{`${
                            localize.modified_by
                          }: ${item?.FieldValue?.ModifiedBy}, ${new Date(
                            item?.FieldValue?.ModificationDate
                          ).toLocaleString(undefined, {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}`}</label>
                        </div>
                      )}
                    </div>
                  </Box>
                </Box>
              </div>
            ))}
        </div>
      ) : (
        <div className="flex justify-center items-center w-full">
          <Typography style={{ marginTop: "20px" }}>
            {localize.no_data}
          </Typography>
        </div>
      )}
    </div>
  );
}
